/**
 * Uprawnienia w aplikacji Staff (ticket #83, kontrakt backendu #82).
 *
 * Dostęp i sekcje są zapisane PER HOTEL w `users/{uid}`:
 *  - `staffAccess[orgId]`: czy konto w ogóle loguje się do Staff w tym hotelu (backend
 *    przelicza z tego claim `staffService`),
 *  - `staffSections[orgId]`: kafelki dashboardu Staff dostępne w tym hotelu,
 *  - `staffOnly`: konto tylko do aplikacji Staff (claim globalny, zmienia go owner).
 * Panel nie pisze claimów ani dokumentu `users`; wszystko idzie przez v3-createTeamMember
 * i v3-updateDocumentTeamMember. Tu jest wyłącznie czysta logika (testy obok).
 */

/** Kategorie zgłoszeń, kolejność jak na dashboardzie Staff. */
export const STAFF_TASK_SECTION_KEYS = [
  'technical', 'housekeeping', 'wakeup', 'guestAsk', 'airportTransfer',
  'taxi', 'lateCheckout', 'minibar', 'pillowMenu', 'others',
] as const;

export const STAFF_ORDER_SECTION_KEYS = ['roomService', 'spa', 'store', 'rental'] as const;

export const STAFF_TOOL_SECTION_KEYS = ['breakfast', 'qrScanner'] as const;

export type StaffSectionKey =
  | (typeof STAFF_TASK_SECTION_KEYS)[number]
  | (typeof STAFF_ORDER_SECTION_KEYS)[number]
  | (typeof STAFF_TOOL_SECTION_KEYS)[number];

export type StaffSectionGroupId = 'tasks' | 'orders' | 'tools';

export const STAFF_SECTION_GROUPS: ReadonlyArray<{ id: StaffSectionGroupId; keys: readonly StaffSectionKey[] }> = [
  { id: 'tasks', keys: STAFF_TASK_SECTION_KEYS },
  { id: 'orders', keys: STAFF_ORDER_SECTION_KEYS },
  { id: 'tools', keys: STAFF_TOOL_SECTION_KEYS },
];

export const STAFF_SECTION_KEYS: readonly StaffSectionKey[] = STAFF_SECTION_GROUPS.flatMap((group) => group.keys);

export const isStaffSectionKey = (value: unknown): value is StaffSectionKey => typeof value === 'string' && (STAFF_SECTION_KEYS as readonly string[]).includes(value);

/** Sekcje w kolejności katalogu, bez duplikatów i nieznanych kluczy. */
export const normalizeStaffSections = (sections: readonly unknown[]): StaffSectionKey[] => STAFF_SECTION_KEYS.filter((key) => sections.includes(key));

/** Stare uprawnienia Staff (claim `staffServicePermissions` sprzed #82). */
export type LegacyStaffPermission = 'reports' | 'roomservice' | 'store' | 'rental' | 'qrscanner' | 'rooms';

/** Stan sekcji w formularzu. */
export interface StaffPermissionsValues {
  staffAccess: boolean;
  staffSections: StaffSectionKey[];
  staffOnly: boolean;
}

/**
 * Dlaczego „Tylko aplikacja Staff” jest zablokowane (null = można zmieniać):
 *  - `self`: nikt nie ogranicza własnego konta (backend: staffPermissions.cannotSetStaffOnlySelf),
 *  - `targetOwner`: właściciel organizacji musi mieć panel (backend: ownerCannotBeStaffOnly),
 *  - `multiOrg`: konto w kilku hotelach; backend wymaga ownera wszystkich, a panel zna tylko
 *    bieżący, więc zmienia to administrator HotelSpot,
 *  - `notOwner`: zmienia to wyłącznie właściciel organizacji (backend: teamMembers.staffOnlyOwnerOnly).
 */
export type StaffOnlyLock = 'self' | 'targetOwner' | 'multiOrg' | 'notOwner';

export const getStaffOnlyLock = (params: {
  actorIsOwner: boolean;
  isSelf: boolean;
  isTargetOwner: boolean;
  /** Liczba organizacji edytowanego konta (`users.organizations`). */
  targetOrganizationsCount?: number;
}): StaffOnlyLock | null => {
  if (params.isSelf) return 'self';
  if (params.isTargetOwner) return 'targetOwner';
  if ((params.targetOrganizationsCount ?? 0) > 1) return 'multiOrg';
  if (!params.actorIsOwner) return 'notOwner';
  return null;
};

/** Pola dokumentu `users/{uid}` potrzebne do odczytu (stary i nowy model). */
export interface StaffPermissionsSource {
  staffService?: boolean;
  staffServicePermissions?: string[];
  staffAccess?: Record<string, unknown>;
  staffSections?: Record<string, unknown>;
  staffOnly?: boolean;
}

/**
 * Sekcje wyliczone ze starego `staffServicePermissions` (konto sprzed backfillu #82).
 * To samo mapowanie co backend: reports → 10 kategorii zgłoszeń, roomservice → roomService,
 * store, rental, qrscanner → qrScanner. `rooms` nie ma odpowiednika w sekcjach.
 */
export const sectionsFromLegacyPermissions = (legacy: readonly unknown[] | undefined): StaffSectionKey[] => {
  const list = Array.isArray(legacy) ? legacy : [];
  const out: StaffSectionKey[] = [];
  if (list.includes('reports')) out.push(...STAFF_TASK_SECTION_KEYS);
  if (list.includes('roomservice')) out.push('roomService');
  if (list.includes('store')) out.push('store');
  if (list.includes('rental')) out.push('rental');
  if (list.includes('qrscanner')) out.push('qrScanner');
  return normalizeStaffSections(out);
};

/**
 * Stare `staffServicePermissions` wyliczone z sekcji. Wysyłane razem z nowymi polami,
 * żeby panel działał też z backendem sprzed #82 (nowy backend przy obecnych mapach je
 * ignoruje). `rooms` przenosimy bez zmian, bo formularz go nie pokazuje.
 */
export const legacyPermissionsFromSections = (
  sections: readonly StaffSectionKey[],
  keepRooms = false,
): LegacyStaffPermission[] => {
  const out: LegacyStaffPermission[] = [];
  if (STAFF_TASK_SECTION_KEYS.some((key) => sections.includes(key))) out.push('reports');
  if (sections.includes('roomService')) out.push('roomservice');
  if (sections.includes('store')) out.push('store');
  if (sections.includes('rental')) out.push('rental');
  if (sections.includes('qrScanner')) out.push('qrscanner');
  if (keepRooms) out.push('rooms');
  return out;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

/**
 * Odczyt uprawnień Staff członka zespołu dla JEDNEGO hotelu. Brak mapy `staffAccess`
 * (konto sprzed backfillu) = wartości wyliczone ze starych pól `staffService` i
 * `staffServicePermissions`.
 */
export const readStaffPermissions = (
  member: StaffPermissionsSource | undefined,
  organizationId: string,
): StaffPermissionsValues => {
  const staffOnly = member?.staffOnly === true;

  if (!isPlainObject(member?.staffAccess)) {
    return {
      staffAccess: member?.staffService === true,
      staffSections: sectionsFromLegacyPermissions(member?.staffServicePermissions),
      staffOnly,
    };
  }

  const sectionsMap = isPlainObject(member?.staffSections) ? member.staffSections : {};
  const sections = sectionsMap[organizationId];
  return {
    staffAccess: member.staffAccess[organizationId] === true,
    staffSections: Array.isArray(sections) ? normalizeStaffSections(sections) : [],
    staffOnly,
  };
};

/** Domyślne wartości przy tworzeniu konta: bez dostępu do Staff. */
export const DEFAULT_STAFF_PERMISSIONS: StaffPermissionsValues = {
  staffAccess: false,
  staffSections: [],
  staffOnly: false,
};

export type StaffPermissionsError = 'sectionsRequired';

/** Czy dostęp albo sekcje różnią się od początkowych (kolejność sekcji bez znaczenia). */
export const hasStaffSectionsChanged = (initial: StaffPermissionsValues, current: StaffPermissionsValues): boolean => {
  if (initial.staffAccess !== current.staffAccess) return true;
  const a = normalizeStaffSections(initial.staffSections);
  const b = normalizeStaffSections(current.staffSections);
  return a.length !== b.length || a.some((key, index) => key !== b[index]);
};

/** Walidacja przy submit: włączony dostęp wymaga co najmniej jednej sekcji. */
export const validateStaffPermissions = (values: StaffPermissionsValues): StaffPermissionsError | null => (values.staffAccess && values.staffSections.length === 0 ? 'sectionsRequired' : null);

/**
 * Czy zalogowany może zmienić `staffOnly`. Backend (#82) przyjmuje to pole wyłącznie od
 * właściciela organizacji, także admin HotelSpot bez własności dostaje 403.
 */
export const canEditStaffOnly = (params: {
  currentUid?: string | null;
  organizationOwner?: string | null;
  isOwnerOfOrganization: boolean;
}): boolean => params.isOwnerOfOrganization
  || (!!params.currentUid && !!params.organizationOwner && params.organizationOwner === params.currentUid);

/** Pola Staff w body v3-createTeamMember / `data` v3-updateDocumentTeamMember. */
export interface StaffPermissionsPayload {
  staffAccess: Record<string, boolean>;
  staffSections: Record<string, StaffSectionKey[]>;
  staffOnly?: boolean;
  /** Zgodność wsteczna z backendem sprzed #82. */
  staffService: boolean;
  staffServicePermissions: LegacyStaffPermission[];
}

/**
 * Konto sprzed backfillu #82: dokument nie ma listy sekcji dla tego hotelu. Wartości w
 * formularzu są wtedy tylko przybliżeniem (np. Spa nadawane claimem `spaOutlets` nie
 * wynika ze starych pól), więc bez zmian w sekcji nic nie wysyłamy.
 */
export const isLegacyStaffPermissions = (
  member: StaffPermissionsSource | undefined,
  organizationId: string,
): boolean => {
  const sectionsMap = isPlainObject(member?.staffSections) ? member.staffSections : {};
  return !Array.isArray(sectionsMap[organizationId]);
};

/**
 * Buduje pola Staff do zapisu (Partial: każde pole może zostać pominięte).
 *  - Klucz map = bieżąca organizacja (backend odrzuca inne).
 *  - Konto sprzed backfillu bez zmian w sekcji (`skipSections`): bez map i starych pól,
 *    żeby zapis profilu nie nadpisał uprawnień przybliżonymi wartościami.
 *  - `staffOnly` tylko gdy wolno go zmieniać (`canEditStaffOnly`) i wartość różni się od
 *    początkowej. Backend wymaga ownera WSZYSTKICH organizacji konta, więc wysyłanie pola
 *    przy każdym zapisie dawałoby 403 dla kont wielohotelowych. Bez dostępu do Staff konto
 *    nie może być `staffOnly` (backend: `staffOnlyRequiresStaffAccess`), więc wtedy `false`.
 */
export const buildStaffPermissionsPayload = (params: {
  organizationId: string;
  values: StaffPermissionsValues;
  canEditStaffOnly: boolean;
  /** Wartość `staffOnly` przy otwarciu formularza (create: false). */
  initialStaffOnly?: boolean;
  /** Pomiń mapy i stare pola (konto sprzed backfillu, sekcja nieruszona). */
  skipSections?: boolean;
  keepLegacyRooms?: boolean;
}): Partial<StaffPermissionsPayload> => {
  const { organizationId, values } = params;
  const sections = normalizeStaffSections(values.staffSections);
  const staffOnly = values.staffAccess && values.staffOnly;
  const sendStaffOnly = params.canEditStaffOnly && staffOnly !== (params.initialStaffOnly ?? false);
  return {
    ...(!params.skipSections && {
      staffAccess: { [organizationId]: values.staffAccess },
      staffSections: { [organizationId]: sections },
      staffService: values.staffAccess,
      staffServicePermissions: values.staffAccess
        ? legacyPermissionsFromSections(sections, params.keepLegacyRooms)
        : [],
    }),
    ...(sendStaffOnly && { staffOnly }),
  };
};

/** Błędy backendu dotyczące „Tylko aplikacja Staff”, pokazywane przy checkboxie. */
export const STAFF_ONLY_FIELD_ERRORS: readonly string[] = [
  'staffPermissions.ownerCannotBeStaffOnly',
  'staffPermissions.cannotSetStaffOnlySelf',
  'staffPermissions.staffOnlyRequiresStaffAccess',
  'staffPermissions.invalidStaffOnly',
  'teamMembers.staffOnlyOwnerOnly',
];

/** Klucz błędu z odpowiedzi backendu v3 (`{ success: false, message }`), jeśli jest. */
export const getBackendErrorKey = (error: unknown): string | null => {
  const message = (error as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message;
  return typeof message === 'string' ? message : null;
};
